import React, { useState, useEffect } from "react";
import { FaShare } from "react-icons/fa"; 
import "./notification.css";  
import config from "../../config"; // Make sure this file contains the backend URL

const LatestNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch notifications from the backend
  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${config.backendUrl}/api/get_notifications`);
        if (!response.ok) throw new Error("Failed to fetch notifications");

        const data = await response.json();

        // Sort the notifications by date in descending order (latest first)
        const sortedData = data.sort((a, b) => new Date(b.notification_date) - new Date(a.notification_date));
        setNotifications(sortedData.slice(0, 4)); // Get only the latest 4 notifications
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  // Convert date to a valid format if it's in YYYY-MM-DD format
  const convertToValidDate = (date) => {
    if (date) {
      if (date.length === 10) {
        const dateWithTime = date + "T00:00:00Z"; // Add the time portion to the date
        return new Date(dateWithTime);
      }
      return new Date(date); // Return the date if it's already in a valid format
    }
    return new Date(); // Return the current date if the date is invalid
  };

  const calculateDaysAgo = (notificationDate) => {
    const notificationDateObj = convertToValidDate(notificationDate);
    
    if (isNaN(notificationDateObj)) {
      console.log('Invalid Date:', notificationDate); // Debugging log
      return "Invalid date"; // Handle invalid date format
    }

    // Set the notification date and current date to midnight UTC for accurate day comparison
    notificationDateObj.setUTCHours(0, 0, 0, 0);
    const currentDate = new Date();
    currentDate.setUTCHours(0, 0, 0, 0);

    const differenceInTime = currentDate - notificationDateObj;
    const differenceInDays = Math.floor(differenceInTime / (1000 * 3600 * 24)); // Convert ms to days
    return differenceInDays >= 0 ? differenceInDays : 0; // Ensure no negative values
  };

  // Function to convert description to a list of items
  const renderDescription = (description) => {
    const items = description
      .split("\n") // Split the description into lines
      .filter(line => line.trim().length > 0) // Remove empty lines
      .map((line, index) => {
        // Check if the line starts with a bullet point marker
        const isBulletPoint = line.startsWith("*") || line.startsWith("-");
        const icon = <FaShare />; // Add the share icon for each item

        // Return each list item
        return (
          <li key={index}>
            <span className="icon text-warning me-2">{icon}</span>
            {isBulletPoint ? line.slice(1).trim() : line} {/* Remove the '*' or '-' from bullet points */}
          </li>
        );
      });

    return <ul className="list">{items}</ul>;
  };

  return (
    <div className="container my-5 latestNotificationsContainer">
      <h2 className="text-center fw-bold mb-4 py-2 heading">LATEST NOTIFICATIONS</h2>

      {loading ? (
        <p className="text-center">Loading notifications...</p>
      ) : error ? (
        <p className="text-center text-danger">{error}</p>
      ) : (
        <div>
          {/* Render latest 4 notifications in 2 rows */}
          <div className="row">
            {notifications.slice(0, 2).map((notification) => (
              <div key={notification.id} className="col-md-6 mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold subHeading mb-0">{notification.title}</h5>
                  <span className="text-muted mb-0">{calculateDaysAgo(notification.date)} days ago</span>
                </div>
                <hr className="horizontalLine" />
                {renderDescription(notification.description)} {/* Render description with bullet points */}
              </div>
            ))}
          </div>

          <div className="row">
            {notifications.slice(2, 4).map((notification) => (
              <div key={notification.id} className="col-md-6 mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold subHeading mb-0">{notification.title}</h5>
                  <span className="text-muted mb-0">{calculateDaysAgo(notification.date)} days ago</span>
                </div>
                <hr className="horizontalLine" />
                {renderDescription(notification.description)} {/* Render description with bullet points */}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LatestNotifications;
